import { Grid, theme } from '@zappyrent/hundop';
import { upperFirst } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Media from 'react-media';
import { Outlet, useParams, useSearchParams } from 'react-router-dom';

import Drawer from '../../components/Drawer';
import Error from '../../components/Error';
import Logo from '../../components/Logo';
import ActionBar from '../../components/UploadDocuments/ActionBar';
import SideBar from '../../components/UploadDocuments/SideBar';
import { UPLOAD_DOCUMENTS_FLOW_STEPS } from '../../shared/constants';
import { useUser } from '../../shared/hooks';
import useBookingData from '../../shared/hooks/useBookingData';
import useStore from '../../store';
import {
  StyledBox,
  StyledBoxesContainer,
  StyledGrid,
  StyledHeader,
  StyledIconWrapperButton,
  StyledMain,
  StyledRoundedHelp,
} from './UploadDocuments.styles';

const UploadDocuments = (): JSX.Element | null => {
  const { t } = useTranslation(['a11y', 'errors']);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const user = useStore(state => state.user);

  const isLoadingUser = useUser();

  const { step } = useParams();
  const [searchParams] = useSearchParams();

  const bookingId = searchParams.get('booking_id');
  const guarantorId = searchParams.get('guarantor_id');

  const bookingData = useBookingData(bookingId);

  if (bookingData !== null && !bookingData.id)
    return (
      <Error errorMessage={upperFirst(t('errors:errorBookingPermission'))} />
    );

  if (!bookingId)
    return (
      <Error errorMessage={upperFirst(t('errors:errorWithoutBookingId'))} />
    );

  if (
    bookingId &&
    step === UPLOAD_DOCUMENTS_FLOW_STEPS.UPLOAD_GUARANTOR_DOCUMENTS &&
    !guarantorId
  )
    return (
      <Error errorMessage={upperFirst(t('errors:errorWithoutGuarantorId'))} />
    );

  return (
    <>
      {!isLoadingUser && (
        <StyledMain>
          <StyledGrid container>
            <StyledHeader>
              <Media
                queries={{
                  mobile: `(max-width: calc(${theme.breakpoints.l} - 1px))`,
                  desktop: `(min-width: ${theme.breakpoints.l})`,
                }}
              >
                {matches => (
                  <>
                    {matches.desktop && <Logo />}
                    {matches.mobile && (
                      <>
                        {step !==
                          UPLOAD_DOCUMENTS_FLOW_STEPS.VERIFY_IDENTITY && (
                          <ActionBar isInHeader />
                        )}
                        <StyledIconWrapperButton
                          onClick={() => setIsDrawerOpen(true)}
                        >
                          <StyledRoundedHelp user={user!.type} />
                        </StyledIconWrapperButton>
                      </>
                    )}
                  </>
                )}
              </Media>
            </StyledHeader>
            <StyledBoxesContainer>
              <StyledBox>
                <Outlet />
              </StyledBox>
              <Media
                queries={{
                  mobile: `(max-width: calc(${theme.breakpoints.l} - 1px))`,
                  desktop: `(min-width: ${theme.breakpoints.l})`,
                }}
              >
                {matches => (
                  <>
                    {matches.desktop && <SideBar />}
                    {matches.mobile && (
                      <Drawer
                        ariaLabel={t('close')}
                        open={isDrawerOpen}
                        handleClose={() => setIsDrawerOpen(false)}
                      >
                        <Grid container>
                          <SideBar mobile />
                        </Grid>
                      </Drawer>
                    )}
                  </>
                )}
              </Media>
            </StyledBoxesContainer>
          </StyledGrid>
        </StyledMain>
      )}
    </>
  );
};

export default UploadDocuments;
